import { BrandConfig } from '../config/brand';

/**
 * Convert hex color to rgba
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Generate CSS custom properties from brand configuration
 */
export function generateThemeCSS(config: BrandConfig): string {
  return `
    :root {
      /* Brand Colors */
      --brand-primary: ${config.colors.primary};
      --brand-primary-dark: ${config.colors.primaryDark};
      --brand-secondary: ${config.colors.secondary};
      --brand-danger: ${config.colors.danger};
      
      /* Primary Color Variants */
      --brand-primary-light: ${hexToRgba(config.colors.primary, 0.1)};
      --brand-primary-border: ${hexToRgba(config.colors.primary, 0.2)};
      --brand-primary-glow: ${hexToRgba(config.colors.primary, 0.3)};
      
      /* Secondary Color Variants */
      --brand-secondary-glow: ${hexToRgba(config.colors.secondary, 0.08)};
      
      /* Danger Color Variants */
      --brand-danger-light: ${hexToRgba(config.colors.danger, 0.1)};

      /* UI Scale Tokens */
      --ui-font-scale: 1;
      --ui-icon-scale: 1;
    }

    :root[data-text-size='small'] {
      --ui-font-scale: 0.92;
      --ui-icon-scale: 0.92;
    }

    :root[data-text-size='medium'] {
      --ui-font-scale: 1;
      --ui-icon-scale: 1;
    }

    :root[data-text-size='large'] {
      --ui-font-scale: 1.08;
      --ui-icon-scale: 1.08;
    }

    html {
      font-size: calc(16px * var(--ui-font-scale));
    }

    .material-symbols-outlined {
      transform: scale(var(--ui-icon-scale));
      transform-origin: center;
      display: inline-block;
    }
  `;
}

/**
 * Inject theme CSS into document head
 */
export function injectThemeCSS(config: BrandConfig): void {
  const styleId = 'brand-theme-variables';
  
  // Remove existing style element if present
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Create and inject new style element
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = generateThemeCSS(config);
  document.head.appendChild(style);
}
